import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useUpload } from '../../hooks/useUpload.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  ADMIN_INPUT_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';
import {
  ENTES_ADMIN_ORGANOS,
  getEnteAdminOrganoLabel,
} from '../../constants/entesAdministrativos.js';

const inputClass = ADMIN_INPUT_CLASS;

const empty = {
  organo: 'consejo_administracion',
  nombre: '',
  cargo: '',
  imagen: '',
  orden: 0,
  estado: true,
};

export default function EntesAdminMiembrosAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filterOrgano, setFilterOrgano] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const { uploadFile, uploading } = useUpload();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  const imagenPath = watch('imagen');

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/entes-administrativos', {
        params: {
          page,
          per_page: perPage,
          search: debouncedSearch,
          organo: filterOrgano || undefined,
        },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch, filterOrgano]);

  function openCreate() {
    setEditingId(null);
    reset({
      ...empty,
      organo: filterOrgano || empty.organo,
    });
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    reset({
      organo: row.organo ?? 'consejo_administracion',
      nombre: row.nombre ?? '',
      cargo: row.cargo ?? '',
      imagen: row.imagen ?? '',
      orden: Number(row.orden) || 0,
      estado: Number(row.estado) === 1,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      organo: values.organo,
      nombre: values.nombre.trim(),
      cargo: values.cargo?.trim() ?? '',
      imagen: values.imagen?.trim() ?? '',
      orden: Number(values.orden) || 0,
      estado: values.estado ? 1 : 0,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/v1/entes-administrativos/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/entes-administrativos', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete('¿Eliminar este miembro?', row.nombre);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/entes-administrativos/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function onPickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file);
      setValue('imagen', path, { shouldValidate: true });
      toastSuccess('Imagen lista', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  async function toggleEstado(row) {
    try {
      const { data } = await api.put(`/v1/entes-administrativos/${row.id}`, {
        organo: row.organo,
        nombre: row.nombre,
        cargo: row.cargo ?? '',
        imagen: row.imagen ?? '',
        orden: Number(row.orden) || 0,
        estado: Number(row.estado) === 1 ? 0 : 1,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Estado actualizado', '');
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  const columns = useMemo(
    () => [
      { key: 'orden', label: 'Orden' },
      {
        key: 'organo',
        label: 'Órgano',
        render: (row) => getEnteAdminOrganoLabel(row.organo),
      },
      { key: 'nombre', label: 'Nombre' },
      { key: 'cargo', label: 'Cargo' },
      {
        key: 'estado',
        label: 'Portal',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleEstado(row)}
            className={adminPublishStatusClasses(Number(row.estado) === 1)}
          >
            {Number(row.estado) === 1 ? 'Visible' : 'Oculto'}
          </button>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button type="button" className={ADMIN_ICON_BTN_CLASS} title="Editar" onClick={() => openEdit(row)}>
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" className={ADMIN_DELETE_BTN_CLASS} title="Eliminar" onClick={() => onDelete(row)}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Entes administrativos</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Miembros del Consejo de Administración y la Junta de Vigilancia en /entes-administrativos.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nuevo miembro
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-slate-600">
          Filtrar por órgano
          <select
            className={`${inputClass} ml-2 mt-1 min-w-[14rem]`}
            value={filterOrgano}
            onChange={(e) => {
              setFilterOrgano(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Todos</option>
            {ENTES_ADMIN_ORGANOS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Editar miembro' : 'Nuevo miembro'}
        wide
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <FormField label="Órgano" error={errors.organo?.message}>
            <select className={inputClass} {...register('organo', { required: 'Selecciona el órgano' })}>
              {ENTES_ADMIN_ORGANOS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Nombre" error={errors.nombre?.message}>
            <input className={inputClass} {...register('nombre', { required: 'El nombre es obligatorio' })} />
          </FormField>
          <FormField label="Cargo" hint="Ej. Presidente, Secretario, Vocal principal">
            <input className={inputClass} {...register('cargo')} />
          </FormField>
          <FormField label="Foto" hint={uploading ? 'Subiendo…' : 'Opcional. Se muestra en la página pública.'}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <input type="file" accept="image/*" onChange={onPickImage} disabled={uploading} />
              <input
                type="text"
                className={`${inputClass} flex-1 text-xs`}
                placeholder="/uploads/..."
                {...register('imagen')}
              />
            </div>
            {imagenPath ? (
              <img
                src={assetUrl(imagenPath)}
                alt=""
                className="mt-3 max-h-24 rounded-lg border border-white/10 object-cover"
              />
            ) : null}
          </FormField>
          <FormField label="Orden en listado">
            <input type="number" className={inputClass} {...register('orden')} />
          </FormField>
          <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-300">
            <input type="checkbox" className="rounded border-white/20" {...register('estado')} />
            Visible en el portal público
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit">Guardar</Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa">
        {previewRow && (
          <div className="space-y-4 text-sm text-slate-300">
            {previewRow.imagen ? (
              <img
                src={assetUrl(previewRow.imagen)}
                alt=""
                className="max-h-32 rounded-lg border border-white/10 object-cover"
              />
            ) : null}
            <p>
              <span className="text-slate-500">Órgano:</span> {getEnteAdminOrganoLabel(previewRow.organo)}
            </p>
            <p>
              <span className="text-slate-500">Nombre:</span> {previewRow.nombre}
            </p>
            <p>
              <span className="text-slate-500">Cargo:</span> {previewRow.cargo || '—'}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
